import type { TFunction } from "i18next";

import type {
  AdherenceDevelopment,
  AdherenceGap,
  AdherenceItems,
  AdherenceScopeItem,
  AdherenceStatus,
} from "@/api/diagnostic";
import {
  ACCEPTANCE_LABELS,
  IMPACT_LABELS,
  ORIGIN_LABELS,
  WORKAROUND_TYPE_LABELS,
} from "@/components/agents/functional/gaps/gapLabels";
import { FORMULA_PREFIXES } from "@/components/agents/functional/scopeActivationFilter";
import { CSV_BOM, csvRow } from "@/lib/csv";

import { UNDECIDED } from "../data/wricefDecisions";
import { makeAdherenceStatusMeta, makeDecisionMeta, makeWricefTypeMeta } from "../diagnosticMeta";
import { NO_VALUE } from "../sectionState";

/**
 * As linhas das três abas do drill-down "Aderência ao padrão" (EF-FUNC-011,
 * RF-08), nas colunas da EF. A tabela e o CSV leem daqui, para a planilha dizer
 * exatamente o que a tela mostra.
 *
 * A ordem é a do índice: scope items por código, desenvolvimentos por status com
 * o fora da conta no fim, GAPs por código. A tela não reordena.
 */

export type AdherenceTab = "scope-items" | "developments" | "gaps";

export const ADHERENCE_TABS: readonly AdherenceTab[] = ["scope-items", "developments", "gaps"];

/** Os chips de cada aba, na ordem da MOC. Fora da conta só existe para o WRICEF. */
export const STATUS_CHIPS: Record<AdherenceTab, readonly AdherenceStatus[]> = {
  "scope-items": ["standard", "extension", "gap", "undecided"],
  developments: ["standard", "extension", "gap", "excluded", "undecided"],
  gaps: [],
};

export interface AdherenceLine {
  /** O código do scope item, o objeto WRICEF ou o código do GAP. */
  key: string;
  /** `null` na aba GAPs, que não tem coluna de status. */
  status: AdherenceStatus | null;
  cells: string[];
}

export function adherenceHeader(tab: AdherenceTab, t: TFunction): string[] {
  if (tab === "scope-items") {
    return [t("Scope item"), t("Description"), t("LoB"), t("Associated WRICEF"), t("Status reason")];
  }
  if (tab === "developments") {
    return [
      t("Object"),
      t("Type"),
      t("Description"),
      t("Architect decision"),
      t("Reason"),
      t("Scope item (standard or associated)"),
      t("Counts in"),
    ];
  }
  return [
    t("ID"),
    t("Origin"),
    t("Reference"),
    t("GAP"),
    t("Workaround type"),
    t("Workaround"),
    t("Impact"),
    t("Client acceptance"),
  ];
}

export function adherenceLines(tab: AdherenceTab, items: AdherenceItems, t: TFunction): AdherenceLine[] {
  if (tab === "scope-items") {
    return items.scope_items.map((item) => ({
      key: item.scope_item_id,
      status: item.status,
      cells: [
        item.scope_item_id,
        item.scope_item_name ?? NO_VALUE,
        item.lob || NO_VALUE,
        (item.related_wricef ?? []).join(", ") || NO_VALUE,
        scopeItemReason(item, t),
      ],
    }));
  }
  if (tab === "developments") {
    const names = new Map(items.scope_items.map((item) => [item.scope_item_id, item.scope_item_name]));
    const decisions = makeDecisionMeta(t);
    const types = makeWricefTypeMeta(t);
    return items.developments.map((item) => ({
      key: item.obj_name,
      status: item.status,
      cells: [
        item.obj_name,
        types[item.wricef_type ?? ""]?.label ?? (item.wricef_type || NO_VALUE),
        item.description || NO_VALUE,
        decisions[item.decision ?? UNDECIDED].label,
        developmentReason(item, t),
        scopeItemCell(item, names, t),
        countsIn(item, t),
      ],
    }));
  }
  return items.gaps.map((gap) => ({ key: gap.code, status: null, cells: gapCells(gap, t) }));
}

/** O motivo do status do scope item, montado do código e dos WRICEF que deram a cor. */
function scopeItemReason(item: AdherenceScopeItem, t: TFunction): string {
  const objects = (item.reason_wricef ?? []).join(", ");
  switch (item.status_reason) {
    case "no_related_wricef":
      return t("Validated scope item, with no associated WRICEF");
    case "related_wricef_extension":
      return t("Extension: {{objects}}", { objects });
    case "related_wricef_gap":
      return t("GAP in WRICEF {{objects}}", { objects });
    case "scope_item_undecided":
      return t("Scope item under decision in the curation");
    case "related_wricef_undecided":
      return t("Associated WRICEF with no decision ({{objects}})", { objects });
  }
}

/**
 * O motivo do "Fora do escopo", com as chaves da decisão do arquiteto
 * (`WricefDecisionSection`). Os dois casos que não têm motivo gravado também
 * se explicam: o objeto em conversão com GAP registrado e o "Fora do escopo"
 * anterior ao motivo obrigatório.
 */
function developmentReason(item: AdherenceDevelopment, t: TFunction): string {
  switch (item.out_of_scope_reason) {
    case "no_usage":
      return t("No usage / obsolete");
    case "discontinued":
      return t("Discontinued process");
    case "no_fit":
      return t("Lack of fit with the standard");
  }
  if (item.status_reason === "gap_registered") return t("GAP registered for the object");
  if (item.status_reason === "out_of_scope_without_reason") return t("Out of scope with no reason");
  return NO_VALUE;
}

/**
 * O scope item standard indicado ou o associado. A associação a um scope item
 * que deixou de ser "Ativar" aparece, mas avisa que não vale mais (§3.3): o
 * objeto voltou a contar em Desenvolvimentos.
 */
function scopeItemCell(
  item: AdherenceDevelopment,
  names: Map<string, string | null | undefined>,
  t: TFunction,
): string {
  if (!item.scope_item) return NO_VALUE;
  const name = names.get(item.scope_item);
  const label = name ? `${item.scope_item} · ${name}` : item.scope_item;
  if (item.scope_item_kind === "standard") return `${label} ${t("(standard)")}`;
  if (item.association_dropped) return `${label} · ${t("no longer Activate")}`;
  return label;
}

function countsIn(item: AdherenceDevelopment, t: TFunction): string {
  if (item.counts_in === "scope_item") return t("In scope item {{id}}", { id: item.scope_item });
  if (item.counts_in === "excluded") return t("Out of the count");
  if (item.status === "undecided") return t("No disposition");
  return t("Developments");
}

function gapCells(gap: AdherenceGap, t: TFunction): string[] {
  const impact = t(IMPACT_LABELS[gap.impact]);
  return [
    gap.code,
    t(ORIGIN_LABELS[gap.origin_type]),
    gap.reference,
    gap.need,
    t(WORKAROUND_TYPE_LABELS[gap.workaround_type]),
    gap.workaround,
    gap.essential ? `${impact} · ${t("essential")}` : impact,
    t(ACCEPTANCE_LABELS[gap.acceptance]),
  ];
}

/** Texto livre que começa como fórmula ganha o apóstrofo, e a planilha o lê como texto. */
function textCell(value: string): string {
  return FORMULA_PREFIXES.some((prefix) => value.startsWith(prefix)) ? `'${value}` : value;
}

/**
 * O CSV da aba aberta, com as linhas que a tabela mostra (o filtro aplicado) e o
 * status por extenso na primeira coluna das abas que têm status.
 */
export function adherenceCsv(tab: AdherenceTab, lines: AdherenceLine[], t: TFunction): string {
  const labels = makeAdherenceStatusMeta(t);
  const withStatus = tab !== "gaps";
  const header = withStatus ? [t("Status"), ...adherenceHeader(tab, t)] : adherenceHeader(tab, t);
  const body = lines.map((entry) => {
    const cells = entry.cells.map((cell) => (cell === NO_VALUE ? "" : textCell(cell)));
    return withStatus && entry.status ? [labels[entry.status], ...cells] : cells;
  });
  return CSV_BOM + [header, ...body].map((cells) => csvRow(cells)).join("\n");
}

/**
 * `myGROW_Diagnostic_Adherence_<aba>_<projeto>_AAAAMMDD_HHMM.csv`, no molde do
 * Excel do detalhe de scope items.
 */
export function adherenceCsvFileName(tab: AdherenceTab, projectName: string | undefined, now: Date): string {
  const pad = (n: number) => String(n).padStart(2, "0");
  const stamp =
    `${now.getFullYear()}${pad(now.getMonth() + 1)}${pad(now.getDate())}` +
    `_${pad(now.getHours())}${pad(now.getMinutes())}`;
  const project = (projectName ?? "").replace(/[^\p{L}\p{N}-]+/gu, "_").replace(/^_+|_+$/g, "");
  return ["myGROW_Diagnostic_Adherence", tab, project, stamp].filter(Boolean).join("_") + ".csv";
}
